/**
 * <brand-toggle> — primary DILAQ / LURK brand switch for the dual-brand
 * storefront.
 *
 * The two tabs are real anchors to each brand's landing page, so the brand
 * context switches server-side on arrival and new-tab / middle-click work like
 * any link. This element does not hijack that navigation — it only keeps the
 * localStorage brand memory in sync so returning visits to neutral pages
 * (cart, search, account, 404) reopen in the last brand world
 * (see the anti-flash script in theme.liquid).
 */
class BrandToggle extends HTMLElement {
  connectedCallback() {
    this.options = Array.from(this.querySelectorAll('[data-brand-option]'));

    // Persist the intended brand on the way out, without blocking the link's
    // own navigation (so Cmd/Ctrl/middle-click still open a new tab normally).
    this.options.forEach((option) => {
      const brand = option.dataset.brandOption;
      option.addEventListener('click', () => this.remember(brand));
      option.addEventListener('auxclick', () => this.remember(brand));
    });

    // Landing in a real brand world updates the memory too, so arriving via a
    // direct link / bookmark (not just a tab click) is remembered afterwards.
    const current = document.documentElement.getAttribute('data-brand');
    if (current === 'dilaq' || current === 'lurk') {
      this.remember(current);
    }
    this.reflect(current);
  }

  remember(brand) {
    if (brand !== 'dilaq' && brand !== 'lurk') return;
    try {
      localStorage.setItem('brand', brand);
    } catch (e) {
      // localStorage unavailable (private mode / blocked) — navigation still works.
    }
  }

  reflect(brand) {
    this.options.forEach((option) => {
      if (option.dataset.brandOption === brand) {
        option.setAttribute('aria-current', 'page');
      } else {
        option.removeAttribute('aria-current');
      }
    });
  }
}

customElements.define('brand-toggle', BrandToggle);
